/**
 * Protected Route Component with Offline Authentication Support
 * Handles both online and offline authentication scenarios
 * Requirements: 1.5, 9.6, 9.7
 */

import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useOfflineAuth } from '../../hooks/useOfflineAuth';
import { OfflineAuthGuard } from './OfflineAuthGuard';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiresOnline?: boolean; // Some routes may require online access
  allowOfflineAccess?: boolean; // Allow access with valid offline token
}

export function ProtectedRoute({ 
  children, 
  requiresOnline = false,
  allowOfflineAccess = true 
}: ProtectedRouteProps) {
  const location = useLocation();
  const { isAuthenticated, checkAuth, isLoading } = useAuthStore();
  const { isOnline, hasValidOfflineToken, canAccessReadingHistory } = useOfflineAuth();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Online scenario: use regular authentication
  if (isOnline) {
    if (!isAuthenticated) {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
    return <>{children}</>;
  }

  // Offline scenario: check if route requires online access
  if (requiresOnline) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-dark-800 rounded-lg p-6 text-center">
          <div className="w-16 h-16 mx-auto bg-yellow-900/20 rounded-full flex items-center justify-center mb-4">
            <svg 
              className="w-8 h-8 text-yellow-500" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" 
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">
            Internet Connection Required
          </h2>
          <p className="text-gray-400 mb-4">
            This feature requires an active internet connection. Please connect to the internet and try again.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Check Connection & Retry
          </button>
        </div>
      </div>
    );
  }

  // Offline scenario with offline access allowed: use OfflineAuthGuard
  if (allowOfflineAccess) {
    return (
      <OfflineAuthGuard requireAuth={true} fallbackPath="/login">
        {children}
      </OfflineAuthGuard>
    );
  }

  // Offline scenario without offline access: redirect to login
  if (!hasValidOfflineToken) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
